import React from "react";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderOpen,
  UploadCloud,
  Search,
  MessageSquareHeart,
  Users,
  CreditCard,
  KeyRound,
  Settings,
  UserCircle,
  History,
  Shield,
} from "lucide-react";
import Header from "../components/Header";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";

export default function AdminLayout() {
  const { t } = useI18n();
  const { isAuthed, user, can } = useAuth();

  if (!isAuthed) {
    return <Navigate to="/admin/login" replace />;
  }

  const rawItems = [
    { to: "/admin", end: true, icon: LayoutDashboard, label: t("admin.overview"), testid: "nav-overview", perm: "stats.read" },
    { to: "/admin/documents", icon: FolderOpen, label: t("admin.documents"), testid: "nav-documents", perm: "docs.read" },
    { to: "/admin/upload", icon: UploadCloud, label: t("admin.upload"), testid: "nav-upload", perm: "docs.write" },
    { to: "/admin/search", icon: Search, label: t("admin.search"), testid: "nav-search", perm: "search.run" },
    { to: "/admin/feedback", icon: MessageSquareHeart, label: t("admin.feedback"), testid: "nav-feedback", perm: "feedback.read" },
    { to: "/admin/users", icon: Users, label: "Utilisateurs", testid: "nav-users", perm: "users.read" },
    { to: "/admin/subscriptions", icon: CreditCard, label: "Abonnements", testid: "nav-subscriptions", perm: "subscriptions.read" },
    { to: "/admin/password-resets", icon: KeyRound, label: "Réinitialisations", testid: "nav-password-resets", perm: "subscriptions.read" },
    { to: "/admin/audit", icon: History, label: "Journal d'audit", testid: "nav-audit", perm: "audit.read" },
    ...(user?.role === "superadmin" ? [
      { to: "/admin/super", icon: Shield, label: "Super Admin", testid: "nav-super", perm: "users.read" },
    ] : []),
    { to: "/admin/settings", icon: Settings, label: "Paramètres", testid: "nav-settings", perm: "settings.read" },
    { to: "/admin/profile", icon: UserCircle, label: "Mon profil", testid: "nav-profile", perm: "profile.change_password" },
  ];
  // Filter by permission — if /me has not yet loaded permissions, allow for now
  const items = user?.permissions?.length
    ? rawItems.filter((it) => can(it.perm))
    : rawItems;

  return (
    <div className="min-h-screen flex flex-col" data-testid="admin-layout">
      <Header />
      <div className="flex-1 max-w-7xl w-full mx-auto px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3 xl:col-span-2">
          <div className="sticky top-24">
            <div className="mb-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-1">
                / {user?.role}
              </div>
              <div className="font-display text-lg text-primary font-semibold leading-tight">
                {user?.full_name}
              </div>
              <div className="font-mono text-[10px] text-foreground/50 truncate">
                {user?.email}
              </div>
            </div>
            <nav className="space-y-1" data-testid="admin-sidebar">
              {items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-sm transition-all ${
                      isActive
                        ? "bg-primary text-white"
                        : "text-foreground/75 hover:bg-primary/5 hover:text-primary"
                    }`
                  }
                  data-testid={it.testid}
                >
                  <it.icon size={15} />
                  {it.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </aside>

        <main className="lg:col-span-9 xl:col-span-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
